import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import { DEFAULT_PRINT_SETTINGS } from "@/types/templeSettings";

/**
 * Pré-visualização do recibo antes de imprimir (UX-027).
 *
 * Antes disto não existia caminho nenhum para ver o que ia sair no papel: o
 * recibo abria em popup, chamava `print()` e `close()` em seguida e sumia da
 * tela antes que alguém lesse; e com o popup bloqueado o botão não fazia nada
 * nem avisava. Quem quisesse saber o que o papel dizia tinha que imprimir para
 * descobrir.
 *
 * Vale só para documento que o FRONTEND monta. PDF gerado no backend
 * (relatório do mês) continua sendo download, por decisão de produto: exibi-lo
 * num iframe dependeria do visualizador embutido do navegador, que não é
 * confiável em Safari/iOS, e mostraria um documento que não é o que se
 * imprime.
 *
 * Singleton por módulo, com um único `<PrintPreviewDialog/>` no `App.vue`
 * reagindo — mesmo desenho do `useUpgradePrompt`. É o que permite os três
 * pontos que emitem recibo (baixa, detalhe e receita nova) continuarem
 * chamando `printReceipt(movement)` e nada mais.
 */
export interface PrintPreviewState {
	/** Documento HTML completo, o MESMO que vai para a impressora. */
	html: string;
}

const preview = ref<PrintPreviewState | null>(null);

export function openPrintPreview(state: PrintPreviewState) {
	// Sessão caída não mostra documento. A prévia é montada fora da condição de
	// autenticação (ver `PrintPreviewDialog`), então sem esta guarda uma abertura
	// tardia poderia pôr um recibo por cima da tela de login.
	if (!useAuthStore().isLoggedIn) return;
	preview.value = state;
}

export function closePrintPreview() {
	preview.value = null;
}

/**
 * O terreiro quer ver antes de imprimir? Padrão LIGADO: o comportamento
 * anterior — documento que some da tela, botão que não responde com popup
 * bloqueado — é justamente o defeito, e não merece ser o padrão de fábrica.
 */
export function isPrintPreviewEnabled(): boolean {
	const authStore = useAuthStore();
	const saved = (authStore.currentTemple?.settings as any)?.print;
	return saved?.previewBeforePrint ?? DEFAULT_PRINT_SETTINGS.previewBeforePrint;
}

export function usePrintPreview() {
	return { preview, openPrintPreview, closePrintPreview, isPrintPreviewEnabled };
}
